import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";
import { en } from "./en";
import { zhCN } from "./zhCN";
import { zhTW } from "./zhTW";

export type { TranslationKey } from "./en";
export * from "./dates";

const STORAGE_KEY = "teacherLocale";

export const LOCALES = ["en", "zh-TW", "zh-CN"] as const;
export type Locale = (typeof LOCALES)[number];

export type Dictionary = Record<keyof typeof en, string>;

const DICTS: Record<Locale, Dictionary> = { en, "zh-TW": zhTW, "zh-CN": zhCN };

// Endonyms, identical in every dictionary, so a user stranded in the wrong language can still
// recognise their own and switch back.
export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  "zh-TW": "繁體中文",
  "zh-CN": "简体中文",
};

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Dictionary;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

const isLocale = (value: string | null): value is Locale => LOCALES.includes(value as Locale);

/** `localStorage` is synchronous, so the first frame is already in the stored language. */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : "en";
  });

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const value = useMemo(() => ({ locale, setLocale, t: DICTS[locale] }), [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/**
 * The active dictionary for code that runs outside React — the transport layer has no context to
 * read. Resolves from the same stored value LocaleProvider initialises with, so the two agree.
 */
export const currentDictionary = (): Dictionary => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return DICTS[isLocale(stored) ? stored : "en"];
};

function useLocaleContext(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) throw new Error("useLocale must be used within LocaleProvider");
  return context;
}

export const useLocale = () => useLocaleContext().locale;

export const useSetLocale = () => useLocaleContext().setLocale;

export const useT = () => useLocaleContext().t;

/** Fills `{name}` placeholders. Unknown placeholders are left as-is rather than blanked. */
export const format = (template: string, vars: Record<string, string | number>) =>
  template.replace(/\{(\w+)\}/g, (match, key) => (key in vars ? String(vars[key]) : match));
